import { useEffect } from 'react';

// Hook to detect clicks outside of a specified element (ref) and Escape key press
export function useOutsideAlerter(
  ref: React.RefObject<HTMLElement | null>,
  callback: () => void,
  excludedClasses: string[] = [],
  triggerClassName: string | null = null
) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const targetElement = event.target as HTMLElement;

      if (
        (triggerClassName && targetElement?.classList.contains(triggerClassName)) ||
        (!triggerClassName &&
          ref.current &&
          !ref.current.contains(targetElement as Node) &&
          !isExcluded(targetElement))
      ) {
        callback();
      }
    }

    function isExcluded(target: HTMLElement): boolean {
      if (excludedClasses.length === 0) return false;

      let currentNode: HTMLElement | null = target;

      while (currentNode) {
        const classList = currentNode.classList;
        if (classList) {
          for (let i = 0; i < classList.length; i++) {
            if (excludedClasses.includes(classList[i])) {
              return true;
            }
          }
        }
        currentNode = currentNode.parentElement;
      }

      return false;
    }

    function handleEscapeKey(event: KeyboardEvent) {
      // Check if the focused element is an input, textarea, or CountriesSelect
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT');

      if (event.key === 'Escape' && !isInputFocused) {
        callback();
      }
    }

    const currentRefHTMLNode = ref?.current as HTMLElement;

    if (currentRefHTMLNode) {
      document.addEventListener('mousedown', handleClickOutside);
      currentRefHTMLNode.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      if (currentRefHTMLNode) {
        currentRefHTMLNode.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      }
    };
  }, [ref, callback, excludedClasses, triggerClassName]);
}
